import { Injectable } from '@angular/core';

import { ACOUSTIC_DEVICE_TYPES, ACOUSTIC_SOUND_TYPES, IAcoustic } from '@models/restore-id/acoustic';
import { ICMR } from '@models/restore-id/cmr';
import { IEDNA } from '@models/restore-id/edna';
import { ILab, PATHOGENS, TESTING_METHODS, TISSUES } from '@models/restore-id/lab';
import { ILocation } from '@models/restore-id/location';
import { IResult } from '@models/restore-id/result';
import { ITrap, LAYOUT_TYPES, TRAP_TYPES } from '@models/restore-id/trap';
import { formatISO } from 'date-fns';

import { DetailService } from '../location/detail/detail.service';
import { LocationService } from '../location/location.service';
import { TrapService } from '../location/trap/trap.service';

function getKeyByValue<T>(object: Record<string, string>, value: string): T {
  return Object.keys(object).find((key) => object[key] === value)! as T;
}

@Injectable()
export class ImportService {
  constructor(
    private _locationService: LocationService,
    private _trapService: TrapService,
    private _detailService: DetailService,
  ) {}

  private mapping = new Map<string, string>([
    ['name_of_location', 'name'],
    ['name', 'name_of_location'],
    ['layout_type', 'layout'],
    ['layout', 'layout_type'],
    ['coord_lat', 'latitude'],
    ['latitude', 'coord_lat'],
    ['coord_long', 'longitude'],
    ['longitude', 'coord_long'],
    ['coord1_lat', 'latitude_1'],
    ['latitude_1', 'coord1_lat'],
    ['coord1_long', 'longitude_1'],
    ['longitude_1', 'coord1_long'],
    ['coord2_lat', 'latitude_2'],
    ['latitude_2', 'coord2_lat'],
    ['coord2_long', 'longitude_2'],
    ['longitude_2', 'coord2_long'],
    ['coord3_lat', 'latitude_3'],
    ['latitude_3', 'coord3_lat'],
    ['coord3_long', 'longitude_3'],
    ['longitude_3', 'coord3_long'],
    ['coord4_lat', 'latitude_4'],
    ['latitude_4', 'coord4_lat'],
    ['coord4_long', 'longitude_4'],
    ['longitude_4', 'coord4_long'],
    ['habitatage', 'age_class_of_habitat'],
    ['age_class_of_habitat', 'habitatage'],
    ['habitatage_comment', 'age_class_of_habitat_comment'],
    ['age_class_of_habitat_comment', 'habitatage_comment'],
    ['surveytype', 'survey_type'],
    ['survey_type', 'surveytype'],

    ['locationname', 'location_name'],
    ['location_name', 'locationname'],

    ['name', 'name_of_trap'],
    ['name_of_trap', 'name'],

    ['trap_name', 'trapname'],
    ['trapname', 'trap_name'],
    ['traptype', 'trap_type'],
    ['trap_type', 'traptype'],
    ['distancetraps', 'distance_between_traps'],
    ['distance_between_traps', 'distancetraps'],
    ['startdate', 'start_date'],
    ['start_date', 'startdate'],
    ['enddate', 'end_date'],
    ['end_date', 'enddate'],
    ['numbertraps', 'amount_of_traps'],
    ['amount_of_traps', 'numbertraps'],
    ['numberdays', 'number_of_days'],
    ['number_of_days', 'numberdays'],
    ['foresttype', 'forest_type'],
    ['forest_type', 'foresttype'],
    ['capturedate', 'capture_date'],
    ['capture_date', 'capturedate'],
    ['startcapture', 'start_capture_date'],
    ['start_capture_date', 'startcapture'],
    ['endcapture', 'end_capture_date'],
    ['end_capture_date', 'endcapture'],
    ['trapid', 'trap_id'],
    ['trap_id', 'trapid'],
    ['trapnumber', 'trap_number'],
    ['trap_number', 'trapnumber'],
    ['trapheight', 'trap_height_above_ground'],
    ['trap_height_above_ground', 'trapheight'],
    ['specimentype', 'specimen_number'],
    ['specimen_number', 'specimentype'],
    ['genus', 'genus_field'],
    ['genus_field', 'genus'],
    ['species', 'species_field'],
    ['species_field', 'species'],
    ['sexcond', 'sex_cond'],
    ['sex_cond', 'sexcond'],
    ['headbody', 'head_body'],
    ['head_body', 'headbody'],
    ['captureremarks', 'capture_remarks'],
    ['capture_remarks', 'captureremarks'],
    ['urinewhatman', 'urine_whatman'],
    ['urine_whatman', 'urinewhatman'],
    ['carcassformol', 'carcass_formal'],
    ['carcass_formal', 'carcassformol'],
    ['samplelabel', 'sample_label'],
    ['sample_label', 'samplelabel'],

    ['tubeid', 'uid_for_vDNA_tube'],
    ['uid_for_vDNA_tube', 'tubeid'],
    ['swabsmistnet', 'swabs_near_to_mistnet'],
    ['swabs_near_to_mistnet', 'swabsmistnet'],

    ['deviceid', 'id_of_the_device'],
    ['id_of_the_device', 'deviceid'],
    ['filecount', 'number_of_files'],
    ['number_of_files', 'filecount'],

    ['labid', 'lab_id'],
    ['lab_id', 'labid'],
    ['test_date', 'testdate'],
    ['testdate', 'test_date'],
    ['testing_method', 'testingmethod'],
    ['testingmethod', 'testing_method'],
    ['sampleid', 'sample_id'],
    ['sample_id', 'sampleid'],
    ['result_id', 'resultid'],
    ['resultid', 'result_id'],
  ]);

  getMappedValue(input: string): string | undefined {
    const key = this.mapping.get(input);
    if (key) {
      return key;
    }
    return input;
  }

  mapReadDataToInstanceData(
    data: any[][],
    importType: string,
    instanceColumns: string[],
    userId: string,
    groupId: string,
    locationName?: string,
  ): ILocation[] | ITrap[] | IAcoustic[] | IEDNA[] | ICMR[] | ILab[] | IResult[] | undefined {
    switch (importType) {
      case 'Location':
        return this._mapReadDataToLocation(data, instanceColumns, userId, groupId);

      case 'Trap':
        return this._mapReadDataToTrap(data, instanceColumns, userId, groupId);

      case 'CMR':
        return this._mapReadDataToCMR(data, instanceColumns, userId, groupId, locationName);

      case 'eDNA':
        return this._mapReadDataToEDNA(data, instanceColumns, userId, groupId, locationName);

      case 'Acoustic':
        return this._mapReadDataToAcoustic(data, instanceColumns, userId, groupId, locationName);

      case 'Lab':
        return this._mapReadDataToLab(data, instanceColumns, userId, groupId, locationName);

      case 'Result':
        return this._mapReadDataToResult(data, instanceColumns, userId, groupId, locationName);

      default:
        return undefined;
    }
  }

  private _mapReadDataToLocation(
    data: any[][],
    instanceColumns: string[],
    userId: string,
    groupId: string,
  ): ILocation[] {
    const instances: ILocation[] = data.map((row) => {
      const instance: ILocation = {
        name: row[instanceColumns.indexOf('name')],
        date: row[instanceColumns.indexOf('date')],
        coord_lat: row[instanceColumns.indexOf('coord_lat')],
        coord_long: row[instanceColumns.indexOf('coord_long')],
        habitatage: row[instanceColumns.indexOf('habitatage')],
        habitatage_comment: row[instanceColumns.indexOf('habitatage_comment')],
        acoustic: row[instanceColumns.indexOf('acoustic')],
        cmr: row[instanceColumns.indexOf('cmr')],
        edna: row[instanceColumns.indexOf('edna')],
        comments: row[instanceColumns.indexOf('comments')],
        createdby: userId,
        groupid: groupId,
      };
      return instance;
    });

    return instances;
  }

  private _mapReadDataToTrap(data: any[][], instanceColumns: string[], userId: string, groupId: string): ITrap[] {
    const instances: ITrap[] = data.map((row) => {
      const instance: ITrap = {
        locationname: row[instanceColumns.indexOf('locationname')],
        traptype:
          getKeyByValue(TRAP_TYPES, row[instanceColumns.indexOf('traptype')]) ??
          row[instanceColumns.indexOf('traptype')],
        startdate: row[instanceColumns.indexOf('startdate')],
        enddate: row[instanceColumns.indexOf('enddate')],
        layout: getKeyByValue(LAYOUT_TYPES, row[instanceColumns.indexOf('layout')]),
        coord_lat: row[instanceColumns.indexOf('coord_lat')],
        coord_long: row[instanceColumns.indexOf('coord_long')],
        coord1_lat: row[instanceColumns.indexOf('coord1_lat')],
        coord1_long: row[instanceColumns.indexOf('coord1_long')],
        coord2_lat: row[instanceColumns.indexOf('coord2_lat')],
        coord2_long: row[instanceColumns.indexOf('coord2_long')],
        coord3_lat: row[instanceColumns.indexOf('coord3_lat')],
        coord3_long: row[instanceColumns.indexOf('coord3_long')],
        coord4_lat: row[instanceColumns.indexOf('coord4_lat')],
        coord4_long: row[instanceColumns.indexOf('coord4_long')],
        numbertraps: row[instanceColumns.indexOf('numbertraps')],
        distancetraps: row[instanceColumns.indexOf('distancetraps')],
        numberdays: row[instanceColumns.indexOf('numberdays')],
        createdby: userId,
        groupid: groupId,
      };
      return instance;
    });

    return instances;
  }

  private _mapReadDataToCMR(
    data: any[][],
    instanceColumns: string[],
    userId: string,
    groupId: string,
    sampleId?: string,
  ): ICMR[] {
    const instances: ICMR[] = data.map((row) => {
      const instance: ICMR = {
        locationname: row[instanceColumns.indexOf('locationname')],
        capturedate: formatISO(row[instanceColumns.indexOf('capturedate')], { representation: 'date' }),
        trapid: row[instanceColumns.indexOf('trapid')],
        traptype: row[instanceColumns.indexOf('traptype')],
        empty: row[instanceColumns.indexOf('empty')],
        genus: row[instanceColumns.indexOf('genus')],
        species: row[instanceColumns.indexOf('species')],
        sex: row[instanceColumns.indexOf('sex')],
        sexcond: row[instanceColumns.indexOf('sexcond')],
        weight: row[instanceColumns.indexOf('weight')],
        headbody: row[instanceColumns.indexOf('headbody')],
        tail: row[instanceColumns.indexOf('tail')],
        organs: row[instanceColumns.indexOf('organs')],
        swabs: row[instanceColumns.indexOf('swabs')],
        ecto: row[instanceColumns.indexOf('ecto')],
        faeces: row[instanceColumns.indexOf('faeces')],
        blood: row[instanceColumns.indexOf('blood')],
        comments: row[instanceColumns.indexOf('comments')],
        createdby: userId,
        groupid: groupId,
        sampleid: row[instanceColumns.indexOf('sampleid')],
      };
      return instance;
    });

    return instances;
  }

  private _mapReadDataToEDNA(
    data: any[][],
    instanceColumns: string[],
    userId: string,
    groupId: string,
    locationName?: string,
  ): IEDNA[] {
    const instances: IEDNA[] = data.map((row) => {
      const instance: IEDNA = {
        tubeid: row[instanceColumns.indexOf('tubeid')],
        date: formatISO(row[instanceColumns.indexOf('date')], { representation: 'date' }),
        numberfiles: row[instanceColumns.indexOf('numberfiles')],
        fliesbait: row[instanceColumns.indexOf('fliesbait')],
        duration: row[instanceColumns.indexOf('duration')],
        amountswabs: row[instanceColumns.indexOf('amountswabs')],
        durationswabs: row[instanceColumns.indexOf('durationswabs')],
        createdby: userId,
        groupid: groupId,
        locationname: row[instanceColumns.indexOf('locationname')],
      };
      return instance;
    });
    return instances;
  }

  private _mapReadDataToAcoustic(
    data: any[][],
    instanceColumns: string[],
    userId: string,
    groupId: string,
    locationName?: string,
  ): IAcoustic[] {
    const instances: IAcoustic[] = data.map((row) => {
      const instance: IAcoustic = {
        deviceid: row[instanceColumns.indexOf('deviceid')],
        startcapture: row[instanceColumns.indexOf('startcapture')],
        endcapture: row[instanceColumns.indexOf('endcapture')],
        filecount: row[instanceColumns.indexOf('filecount')],
        soundtype: getKeyByValue(ACOUSTIC_SOUND_TYPES, row[instanceColumns.indexOf('soundtype')]),
        sizedata: row[instanceColumns.indexOf('sizedata')],
        devicetype: getKeyByValue(ACOUSTIC_DEVICE_TYPES, row[instanceColumns.indexOf('devicetype')]),
        height: row[instanceColumns.indexOf('height')],
        softwareversion: row[instanceColumns.indexOf('softwareversion')],
        comments: row[instanceColumns.indexOf('comments')],
        createdby: userId,
        groupid: groupId,
        locationname: row[instanceColumns.indexOf('locationname')],
      };
      return instance;
    });

    return instances;
  }

  private _mapReadDataToLab(
    data: any[][],
    instanceColumns: string[],
    userId: string,
    groupId: string,
    sampleId?: string,
  ): ILab[] {
    const instances: ILab[] = data.map((row) => {
      const instance: ILab = {
        sampleid: row[instanceColumns.indexOf('sampleid')],
        labid: row[instanceColumns.indexOf('labid')],
        test_date: formatISO(row[instanceColumns.indexOf('testdate')], { representation: 'date' }),
        testing_method: getKeyByValue(TESTING_METHODS, row[instanceColumns.indexOf('testingmethod')]),
        pathogen: getKeyByValue(PATHOGENS, row[instanceColumns.indexOf('pathogen')]),
        tissue: getKeyByValue(TISSUES, row[instanceColumns.indexOf('tissue')]),
        result: row[instanceColumns.indexOf('result')],
        createdby: userId,
        groupid: groupId,
      };
      return instance;
    });

    return instances;
  }

  private _mapReadDataToResult(
    data: any[][],
    instanceColumns: string[],
    userId: string,
    groupId: string,
    tubeid?: string,
  ): IResult[] {
    const instances: IResult[] = data.map((row) => {
      const instance: IResult = {
        tubeid: row[instanceColumns.indexOf('tubeid')],
        resultid: row[instanceColumns.indexOf('resultid')],
        species: row[instanceColumns.indexOf('species')],
        createdby: userId,
        groupid: groupId,
      };
      return instance;
    });

    return instances;
  }

  async importData(
    data: any[][],
    importType: string,
    instanceColumns: string[],
    userId: string,
    groupId: string,
    sampleId?: string,
  ): Promise<string[]> {
    const errorMessages: string[] = [];
    const instances = this.mapReadDataToInstanceData(data, importType, instanceColumns, userId, groupId, sampleId);

    switch (importType) {
      case 'Location':
        await this._locationService.importLocation(instances as ILocation[]);
        break;

      case 'Trap':
        await this._trapService.importTrap(instances as ITrap[]);
        break;

      case 'CMR':
        await this._detailService.importCMR(instances as ICMR[]);
        break;

      case 'eDNA':
        await this._detailService.importEDNA(instances as IEDNA[]);
        break;

      case 'Acoustic':
        await this._detailService.importAcoustic(instances as IAcoustic[]);
        break;

      case 'Lab':
        await this._detailService.importLab(instances as ILab[]);
        break;

      case 'Result':
        await this._detailService.importResult(instances as IResult[]);
        break;

      default:
        break;
    }

    return errorMessages;
  }

  mapColumnsToLocation() {
    return [
      { name: 'name', type: 'string', required: true },
      { name: 'date', type: 'date', required: true, options: { operation: 'less', value: ['now'] } },
      { name: 'coord_lat', type: 'latitude', required: true },
      { name: 'coord_long', type: 'longitude', required: true },
      { name: 'habitatage', type: 'string', required: true, },
      { name: 'habitatage_comment', type: 'string', required: false },
      { name: 'acoustic', type: 'boolean', required: false },
      { name: 'cmr', type: 'boolean', required: false },
      { name: 'edna', type: 'boolean', required: false },
      { name: 'comments', type: 'string', required: false },
      { name: 'createdby', type: 'string', required: false },
      { name: 'groupid', type: 'string', required: false },
    ];
  }

  mapColumnsToTrap() {
    return [
      { name: 'locationname', type: 'string', required: true },
      {
        name: 'traptype',
        type: 'string',
        required: true,
        options: { operation: 'in', fallback: true, value: Object.values(TRAP_TYPES) },
      },
      { name: 'startdate', type: 'date', required: true, options: { operation: 'less', value: ['now'] } },
      { name: 'enddate', type: 'date', required: true, options: { operation: 'less', value: ['now'] } },
      {
        name: 'layout',
        type: 'string',
        required: true,
        options: { operation: 'in', value: Object.values(LAYOUT_TYPES) },
      },
      { name: 'coord_lat', type: 'latitude', required: true },
      { name: 'coord_long', type: 'longitude', required: true },
      { name: 'coord1_lat', type: 'latitude', required: false },
      { name: 'coord1_long', type: 'longitude', required: false },
      { name: 'coord2_lat', type: 'latitude', required: false },
      { name: 'coord2_long', type: 'longitude', required: false },
      { name: 'coord3_lat', type: 'latitude', required: false },
      { name: 'coord3_long', type: 'longitude', required: false },
      { name: 'coord4_lat', type: 'latitude', required: false },
      { name: 'coord4_long', type: 'longitude', required: false },
      { name: 'numbertraps', type: 'integer', required: false },
      { name: 'distancetraps', type: 'integer', required: false },
      { name: 'numberdays', type: 'integer', required: false },
      { name: 'comments', type: 'string', required: false },
    ];
  }

  mapColumnsToCMR() {
    return [
      { name: 'locationname', type: 'string', required: true },
      { name: 'sampleid', type: 'string', required: true },
      { name: 'capturedate', type: 'timestamp', required: false, options: { operation: 'less', value: ['now'] } },
      { name: 'trapid', type: 'string', required: false },
      { name: 'traptype', type: 'string', required: false },
      { name: 'empty', type: 'boolean', required: false },
      { name: 'genus', type: 'string', required: true },
      { name: 'species', type: 'string', required: false },
      { name: 'sex', type: 'string', required: false, options: { operation: 'in', value: ['M', 'F'] } },
      { name: 'sexcond', type: 'string', required: false },
      { name: 'weight', type: 'integer', required: false },
      { name: 'headbody', type: 'string', required: false },
      { name: 'tail', type: 'string', required: false },
      { name: 'organs', type: 'boolean', required: false },
      { name: 'swabs', type: 'boolean', required: false },
      { name: 'ecto', type: 'boolean', required: false },
      { name: 'faeces', type: 'boolean', required: false },
      { name: 'blood', type: 'boolean', required: false },
      { name: 'comments', type: 'string', required: false },
      { name: 'createdby', type: 'string', required: false },
      { name: 'groupid', type: 'string', required: false },
    ];
  }

  mapColumnsToEDNA() {
    return [
      { name: 'locationname', type: 'string', required: true },
      { name: 'tubeid', type: 'string', required: true },
      { name: 'date', type: 'timestamp', required: false, options: { operation: 'less', value: ['now'] } },
      { name: 'numberfiles', type: 'integer', required: false },
      { name: 'fliesbait', type: 'string', required: false },
      { name: 'duration', type: 'integer', required: false },
      { name: 'amountswabs', type: 'integer', required: false },
      { name: 'durationswabs', type: 'integer', required: false },
      { name: 'createdby', type: 'string', required: false },
      { name: 'groupid', type: 'string', required: false },
    ];
  }

  mapColumnsToAcoustic() {
    return [
      { name: 'locationname', type: 'string', required: true },
      { name: 'deviceid', type: 'string', required: false },
      { name: 'startcapture', type: 'timestamp', required: false, options: { operation: 'less', value: ['now'] } },
      { name: 'endcapture', type: 'timestamp', required: false, options: { operation: 'less', value: ['now'] } },
      { name: 'filecount', type: 'integer', required: false },
      {
        name: 'soundtype',
        type: 'string',
        required: false,
        options: { operation: 'in', value: Object.values(ACOUSTIC_SOUND_TYPES) },
      },
      { name: 'sizedata', type: 'number', required: false },
      {
        name: 'devicetype',
        type: 'string',
        required: false,
        options: { operation: 'in', value: Object.values(ACOUSTIC_DEVICE_TYPES) },
      },
      { name: 'height', type: 'number', required: false },
      { name: 'softwareversion', type: 'string', required: false },
      { name: 'comments', type: 'string', required: false },
      { name: 'sampleid', type: 'string', required: false },
      { name: 'createdby', type: 'string', required: false },
      { name: 'groupid', type: 'string', required: false },
    ];
  }

  mapColumnsToLab() {
    return [
      { name: 'sampleid', type: 'string', required: true },
      { name: 'labid', type: 'string', required: true },
      { name: 'testdate', type: 'date', required: false, options: { operation: 'less', value: ['now'] } },
      {
        name: 'testingmethod',
        type: 'string',
        required: false,
        options: { operation: 'in', value: Object.values(TESTING_METHODS) },
      },
      {
        name: 'tissue',
        type: 'string',
        required: false,
        options: { operation: 'in', value: Object.values(TISSUES) },
      },
      {
        name: 'pathogen',
        type: 'string',
        required: false,
        options: { operation: 'in', value: Object.values(PATHOGENS) },
      },
      { name: 'result', type: 'boolean', required: true },
      { name: 'createdby', type: 'string', required: false },
      { name: 'groupid', type: 'string', required: false },
    ];
  }

  mapColumnsToResult() {
    return [
      { name: 'tubeid', type: 'string', required: true },
      { name: 'resultid', type: 'string', required: true },
      { name: 'species', type: 'string', required: true },
      { name: 'createdby', type: 'string', required: false },
      { name: 'groupid', type: 'string', required: false },
    ];
  }
}
